import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { FUTURE } from '../content/siteContent';
import { useReducedMotion } from '../hooks/useMotionPrefs';

export default function Future() {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (!reduced) {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: ref.current,
            start: 'top 65%',
            toggleActions: 'play none none reverse',
          },
        });
        // paths converge → screen simplifies → typography appears (auto-plays on entry)
        tl.fromTo('.future-line', { yPercent: 120 }, { yPercent: 0, stagger: 0.15, duration: 1.1, ease: 'power3.out' }, 0.2)
          .fromTo('.future-body', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.9 }, 0.9);
      }

      // Particle field — scattered light converging back to one source
      const canvas = canvasRef.current;
      if (!canvas || reduced) return;
      const c2d = canvas.getContext('2d');
      let w;
      let h;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const resize = () => {
        w = canvas.offsetWidth;
        h = canvas.offsetHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        c2d.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      window.addEventListener('resize', resize);

      const N = window.innerWidth < 768 ? 45 : 90;
      const pts = Array.from({ length: N }, () => ({
        x: Math.random(),
        y: Math.random(),
        a: Math.random() * Math.PI * 2,
        r: 0.05 + Math.random() * 0.22,
        s: 0.001 + Math.random() * 0.002,
      }));
      const proxy = { p: 0 };
      let active = false;
      ScrollTrigger.create({
        trigger: ref.current,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: (st) => {
          active = st.isActive;
        },
      });
      // particles converge automatically once the section enters
      gsap.to(proxy, {
        p: 1,
        duration: 8,
        ease: 'power1.inOut',
        scrollTrigger: { trigger: ref.current, start: 'top 75%', toggleActions: 'play none none none' },
      });

      let raf;
      const draw = () => {
        raf = requestAnimationFrame(draw);
        if (!active) return;
        c2d.clearRect(0, 0, w, h);
        const cx = w / 2;
        const cy = h / 2;
        const conv = Math.min(1, Math.max(0, (proxy.p - 0.15) / 0.6));
        const e = conv * conv * (3 - 2 * conv);
        for (const p of pts) {
          p.a += p.s;
          const sx = p.x * w + Math.cos(p.a) * 26;
          const sy = p.y * h + Math.sin(p.a) * 26;
          const tx = cx + Math.cos(p.a * 3) * p.r * w * 0.35;
          const ty = cy + Math.sin(p.a * 3) * p.r * h * 0.35;
          const x = sx + (tx - sx) * e;
          const y = sy + (ty - sy) * e;
          c2d.beginPath();
          c2d.arc(x, y, 1.3, 0, Math.PI * 2);
          c2d.fillStyle = `rgba(71,160,217,${0.18 + 0.6 * e})`;
          c2d.fill();
        }
        // the single source appears as everything converges
        if (e > 0.05) {
          c2d.beginPath();
          c2d.arc(cx, cy, 2 + e * 5, 0, Math.PI * 2);
          c2d.fillStyle = `rgba(71,160,217,${e})`;
          c2d.fill();
        }
      };
      raf = requestAnimationFrame(draw);

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', resize);
      };
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="future"
      data-testid="future-section"
      className="relative h-screen overflow-hidden bg-[#000000]"
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />

      <div className="absolute left-5 top-24 z-10 md:left-12 md:top-28">
        <SectionTag number="11" label="The Future" />
      </div>

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-5 text-center">
        <h2
          className="font-display font-bold leading-[0.95] tracking-tight text-white"
          style={{ fontSize: 'clamp(2rem, 6.5vw, 5.5rem)' }}
        >
          {FUTURE.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <span className="future-line block will-change-transform">{line}</span>
            </span>
          ))}
        </h2>
        <p className="future-body mt-8 max-w-lg text-sm leading-relaxed text-white/65 md:text-base">{FUTURE.body}</p>
      </div>
    </section>
  );
}
